import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import Pagination from "@/components/feature/Pagination";
import FilterSidebar from "@/pages/anime/components/FilterSidebar";
import { animeList } from "@/mocks/anime";

const PAGE_SIZE = 8;

const sortOptions = [
  { value: "default", label: "最近更新" },
  { value: "popular", label: "人气最高" },
  { value: "rating", label: "评分最高" },
  { value: "newest", label: "最新上线" },
];

export default function AnimeList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const type = searchParams.get("type") || "";
  const region = searchParams.get("region") || "";
  const year = searchParams.get("year") || "";
  const q = searchParams.get("q") || "";
  const sort = searchParams.get("sort") || "default";
  const page = Math.max(1, Number(searchParams.get("page") || "1"));

  const types = useMemo(() => Array.from(new Set(animeList.map((a) => a.type))), []);
  const regions = useMemo(() => Array.from(new Set(animeList.map((a) => a.region))), []);
  const years = useMemo(
    () => Array.from(new Set(animeList.map((a) => String(a.year)))).sort((a, b) => Number(b) - Number(a)),
    []
  );

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    let list = [...animeList];
    if (type) list = list.filter((a) => a.type === type);
    if (region) list = list.filter((a) => a.region === region);
    if (year) list = list.filter((a) => String(a.year) === year);
    if (q) {
      const kw = q.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.summary.toLowerCase().includes(kw) ||
          a.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    switch (sort) {
      case "popular":
        list.sort((a, b) => b.popularity - a.popularity);
        break;
      case "rating":
        list.sort((a, b) => b.rating - a.rating);
        break;
      case "newest":
        list.sort((a, b) => b.year - a.year);
        break;
      default:
        list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    }
    return list;
  }, [type, region, year, q, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
    setMobileFilterOpen(false);
  };

  const titleText = q ? `“${q}” 的搜索结果` : type ? `${type}动漫` : "全部动漫作品";

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "动漫", to: "/anime" },
          { label: type || (q ? "搜索" : "全部") },
        ]}
      />

      <div className="mt-5 mb-6 md:mb-8">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {titleText}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-3xl">
          浏览全部动漫作品，可通过类型、地区与年份筛选，或按人气、评分与更新时间排序，快速定位你想看的作品。
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
        {/* Sidebar (desktop) */}
        <div className="hidden lg:block">
          <FilterSidebar
            type={type}
            region={region}
            year={year}
            types={types}
            regions={regions}
            years={years}
            resultCount={filtered.length}
            onChange={updateParam}
            onReset={resetAll}
          />
        </div>

        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 mb-4 md:mb-5">
            <button
              type="button"
              onClick={() => setMobileFilterOpen((v) => !v)}
              className="lg:hidden inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-800 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
            <span className="hidden lg:block text-sm text-foreground-500">
              共 {filtered.length} 部作品
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-sm text-foreground-500 whitespace-nowrap hidden sm:block">
                排序
              </label>
              <select
                value={sort}
                onChange={(e) => updateParam("sort", e.target.value)}
                className="h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Mobile sidebar */}
          {mobileFilterOpen && (
            <div className="lg:hidden mb-5">
              <FilterSidebar
                type={type}
                region={region}
                year={year}
                types={types}
                regions={regions}
                years={years}
                resultCount={filtered.length}
                onChange={updateParam}
                onReset={resetAll}
              />
            </div>
          )}

          {paged.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                {paged.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
              <div className="mt-8 md:mt-10">
                <Pagination
                  current={currentPage}
                  total={totalPages}
                  onChange={(p) => updateParam("page", String(p))}
                />
              </div>
            </>
          ) : (
            <div className="py-16 md:py-24 flex flex-col items-center justify-center text-center rounded-lg bg-background-100 border border-background-200">
              <span className="w-16 h-16 rounded-full bg-background-200 text-foreground-400 flex items-center justify-center mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">
                没有找到匹配的动漫
              </h3>
              <p className="text-sm text-foreground-500 mb-5 max-w-sm">
                试试调整筛选条件或更换关键词，或许就能找到你想看的内容。
              </p>
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}